import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';
import {
  usePlayerActorScene,
  usePlayerResolvedAnimation,
  usePlayerSettings,
} from '@cartwhl/3d/player';

import { noticesStore, userStore } from '../../../../stores/global';
import { CanvasLayoutSidePanel } from '../../../CanvasLayout';
import {
  ENABLE_CHARACTER_PICKER_PANEL,
  ENABLE_HISTORY_PANEL,
} from '../../feature-flags';
import { useStore as useKeyPoseStore } from '../../keyPoses';
import {
  KEY_POSE_CHARACTER_SWAP_WARNING_MESSAGE,
  clearKeyPosesForCharacterSwap,
  isKeyPoseCharacterSwapWarningActive,
  isKeyPoseEditingSupportedCharacterId,
} from '../../keyPoses/keyPoseCharacterSwap';
import {
  selectAppliedKeyPoseComparisonGhostHistoryEntries,
  selectAvailableKeyPoseComparisonGhostHistoryEntries,
  selectPlayableKeyPoseComparisonGhostHistoryEntries,
} from '../../keyPoses/keyPoseComparisonGhostUtils';
import { getSourceBvhForTimeline } from '../../keyPoses/keyPoseFrameMapping';
import { resolveKeyPoseMotionContext } from '../../keyPoses/keyPoseMotionUtils';
import { createRecoveredKeyPoseInputsFromApiKeyPoses } from '../../keyPoses/keyPoseRecovery';
import { selectKeyPoseTimelineItems } from '../../keyPoses/keyPoseSelectors';
import { useStore } from '../../motionEditorStore';
import { useStore as useRepathingStore } from '../../repathing/repathingStore';
import CharacterPanel from './CharacterPanel';
import HistoryPanel from './HistoryPanel';
import StageOptionsPanel from './StageOptionsPanel';
import type { MotionEditorPanel } from './types';

export interface SidePanelsProps {
  openPanel: MotionEditorPanel | null;
  onOpenPanelChange: (panel: MotionEditorPanel | null) => void;
}

export default function SidePanels({
  openPanel,
  onOpenPanelChange,
}: SidePanelsProps) {
  const store = useStore();
  const keyPoseStore = useKeyPoseStore();
  const repathingStore = useRepathingStore();
  const { isCartwheeler } = useSnapshot(userStore.state);
  const {
    settings: { environment, playback: playerPlayback, scene: playerScene },
    setSettings: setPlayerSettings,
  } = usePlayerSettings();
  const { characters, playback } = store.state;
  const { availableCharacters, selectedCharacter, characterSettings } =
    characters;
  const { sceneActors, selectedReferenceName } = playback;
  const characterPanelReferenceName =
    selectedReferenceName ??
    (sceneActors.length === 1 ? (sceneActors[0]?.referenceName ?? null) : null);
  const actorLabelByReferenceName = Object.fromEntries(
    sceneActors.map((actor) => [actor.referenceName, actor.label]),
  );
  const ghostMotionContext = resolveKeyPoseMotionContext({
    scene: store.state.scene,
    playback,
    keyPoseReferenceName:
      repathingStore.state.controlsMode === 'body'
        ? keyPoseStore.state.activeReferenceName
        : null,
  });
  const historyReferenceName =
    ghostMotionContext?.referenceName ??
    selectedReferenceName ??
    (sceneActors.length === 1 ? (sceneActors[0]?.referenceName ?? null) : null);
  const historyActorLabel = historyReferenceName
    ? (actorLabelByReferenceName[historyReferenceName] ?? historyReferenceName)
    : null;
  const historyActorScene = usePlayerActorScene(
    historyReferenceName ?? undefined,
  );
  const characterPanelActorScene = usePlayerActorScene(
    characterPanelReferenceName ?? undefined,
  );
  const isMhrCharacter =
    Boolean(selectedCharacter?.identityJsonURL) ||
    Boolean(characterPanelActorScene && isMhrScene(characterPanelActorScene));
  const historyResolvedAnimation = usePlayerResolvedAnimation(
    historyReferenceName ?? undefined,
  );
  const historySourceBvh = getSourceBvhForTimeline(
    historyResolvedAnimation.bvh,
    historyResolvedAnimation.bvhs,
    ghostMotionContext?.timelineIndex,
  );
  const ghostAvailableEntryCount =
    selectAvailableKeyPoseComparisonGhostHistoryEntries(
      keyPoseStore.state.generationHistory,
      ghostMotionContext,
    ).length;
  const appliedHistoryJobIDs =
    selectAppliedKeyPoseComparisonGhostHistoryEntries(
      keyPoseStore.state.generationHistory,
      ghostMotionContext,
    ).map((entry) => entry.jobID);
  const hintedAppliedHistoryJobID =
    keyPoseStore.state.comparisonGhost.appliedHistoryJobID;
  const appliedHistoryJobIDSet = new Set([
    ...appliedHistoryJobIDs,
    ...(hintedAppliedHistoryJobID ? [hintedAppliedHistoryJobID] : []),
  ]);
  const ghostSelectableJobIDs =
    selectPlayableKeyPoseComparisonGhostHistoryEntries(
      keyPoseStore.state.generationHistory,
      ghostMotionContext,
    )
      .map((entry) => entry.jobID)
      .filter((jobID) => !appliedHistoryJobIDSet.has(jobID));
  const pendingKeyPoseCharacterSwapRef = useRef<{
    characterID: string;
    referenceName: string | null | undefined;
  } | null>(null);

  useEffect(() => {
    if (openPanel !== 'character') {
      return;
    }
    if (availableCharacters.status !== 'initial') {
      return;
    }

    void store.loadAvailableCharacters();
  }, [availableCharacters.status, openPanel, store]);

  return (
    <>
      {ENABLE_CHARACTER_PICKER_PANEL && (
        <CanvasLayoutSidePanel
          isOpen={openPanel === 'character'}
          title="Character"
          closePanel={() => onOpenPanelChange(null)}
          loading={availableCharacters.status === 'pending'}
          statusText={
            availableCharacters.status === 'error'
              ? 'Failed to load characters'
              : undefined
          }
        >
          <CharacterPanel
            availableCharacters={availableCharacters}
            selectedReferenceName={selectedReferenceName}
            selectedCharacter={selectedCharacter}
            characterSettings={characterSettings}
            isMhrCharacter={isMhrCharacter}
            onSelectCharacter={async (characterID, referenceName) => {
              const targetReferenceName =
                referenceName ??
                selectedReferenceName ??
                sceneActors[0]?.referenceName;
              const hasExistingKeyPoses =
                selectKeyPoseTimelineItems(
                  keyPoseStore.state,
                  targetReferenceName,
                ).length > 0;
              const requiresKeyPoseRemoval =
                hasExistingKeyPoses &&
                !isKeyPoseEditingSupportedCharacterId(characterID);

              if (requiresKeyPoseRemoval) {
                const isConfirmingPendingSwap =
                  isKeyPoseCharacterSwapWarningActive() &&
                  pendingKeyPoseCharacterSwapRef.current?.characterID ===
                    characterID &&
                  pendingKeyPoseCharacterSwapRef.current?.referenceName ===
                    targetReferenceName;

                if (!isConfirmingPendingSwap) {
                  pendingKeyPoseCharacterSwapRef.current = {
                    characterID,
                    referenceName: targetReferenceName,
                  };
                  noticesStore.warning(KEY_POSE_CHARACTER_SWAP_WARNING_MESSAGE);
                  return false;
                }

                pendingKeyPoseCharacterSwapRef.current = null;
                if (targetReferenceName) {
                  await clearKeyPosesForCharacterSwap({
                    keyPoseStore,
                    motionEditorState: store.state,
                    referenceName: targetReferenceName,
                  });
                }
              } else {
                pendingKeyPoseCharacterSwapRef.current = null;
              }

              await store.selectCharacter(
                characterID,
                referenceName ?? undefined,
              );
            }}
            onCharacterSettingsChange={(nextSettings, referenceName) => {
              store.setCharacterSettings(nextSettings, referenceName);
            }}
          />
        </CanvasLayoutSidePanel>
      )}

      {ENABLE_HISTORY_PANEL && (
        <CanvasLayoutSidePanel
          isOpen={openPanel === 'history'}
          title="History"
          closePanel={() => onOpenPanelChange(null)}
          loading={keyPoseStore.state.generationHistoryLoading}
        >
          <HistoryPanel
            actorLabel={historyActorLabel}
            appliedHistoryJobIDs={Array.from(appliedHistoryJobIDSet)}
            canApplyHistoryGeneration={keyPoseStore.state.hasApiKey}
            ghostSelectableJobIDs={ghostSelectableJobIDs}
            ghostSelectionVisible={ghostAvailableEntryCount > 1}
            ghostSelectionLoadingJobID={
              keyPoseStore.state.comparisonGhost.loadingHistoryJobID
            }
            hasPendingGeneration={
              keyPoseStore.state.generation.status === 'pending' ||
              repathingStore.state.generation.status === 'pending'
            }
            historyEntries={keyPoseStore.state.generationHistory}
            loading={keyPoseStore.state.generationHistoryLoading}
            selectedGhostHistoryJobID={
              keyPoseStore.state.comparisonGhost.selectedHistoryJobID
            }
            onApplyHistoryGeneration={async (entry) => {
              const canHydrateHistoryKeyPoses =
                entry.referenceName === historyReferenceName &&
                entry.timelineIndex === ghostMotionContext?.timelineIndex;
              const result = await keyPoseStore.applyHistoryGenerationToScene({
                entry,
                createRecoveredKeyPoses: (keyPoses) =>
                  createRecoveredKeyPoseInputsFromApiKeyPoses({
                    actorScene: canHydrateHistoryKeyPoses
                      ? historyActorScene
                      : null,
                    keyPoses,
                    sourceBvh: canHydrateHistoryKeyPoses
                      ? historySourceBvh
                      : null,
                  }),
              });
              return result.ok
                ? { ok: true }
                : { ok: false, error: result.error };
            }}
            onClearGhostHistoryEntry={() => {
              keyPoseStore.clearComparisonGhostHistoryEntry();
            }}
            onSelectGhostHistoryEntry={async (entry) => {
              return keyPoseStore.selectComparisonGhostHistoryEntry({
                entry,
                scope: ghostMotionContext,
              });
            }}
          />
        </CanvasLayoutSidePanel>
      )}

      <CanvasLayoutSidePanel
        isOpen={openPanel === 'stage'}
        title="Stage options"
        closePanel={() => onOpenPanelChange(null)}
      >
        <StageOptionsPanel
          environment={environment}
          keyPoseGhostMaterialVariant={
            keyPoseStore.state.ghostOptions.materialVariant
          }
          showActorNameTags={playerScene.showActorNameTags}
          showActorNameTagsOption={sceneActors.length > 1}
          showKeyPoseGhostMaterialVariantOption={isCartwheeler === true}
          loopPlayback={playerPlayback.loop}
          onEnvironmentChange={(nextEnvironment) => {
            setPlayerSettings({
              environment: nextEnvironment,
            });
          }}
          onKeyPoseGhostMaterialVariantChange={(variant) => {
            keyPoseStore.setKeyPoseGhostMaterialVariant(variant);
          }}
          onShowActorNameTagsChange={(showActorNameTags) => {
            setPlayerSettings({
              scene: {
                showActorNameTags,
              },
            });
          }}
          onLoopPlaybackChange={(loop) => {
            setPlayerSettings({
              playback: {
                loop,
              },
            });
          }}
        />
      </CanvasLayoutSidePanel>
    </>
  );
}
